import { useCallback, useMemo } from 'react';
import { useTranslation } from '@shared/core';
import { useAuth } from '@shared/components/AuthProvider';
import { useProductRows } from '@apps/admin/routes/Dashboard/components/DashboardContentV2/hooks/useProductRows';
import { useEventNameMatch } from '@shared/components/AuthProvider/AuthProvider.utils';
import { format, parseISO } from 'date-fns';
import { useAdminRegistryTelemetry } from '@apps/registry/admin/AdminRegistry.telemetry';
import type { BurgerMenuProps } from './components/BurgerMenu/BurgerMenu';
import { useGetUniversalBurgerMenuDataQuery } from './UniversalBurgerMenu.graphql.generated';
import { extractEventPhoto, extractHeaderLayoutPhoto, resolvePhotoUrl } from '@shared/utils/eventPhoto';

export const GLOBAL_NAV_MENU = 'GlobalNavMenu';

export function useUniversalBurgerMenuController() {
  const { provideLogout } = useAuth();
  const telemetry = useAdminRegistryTelemetry();
  const { eventName } = useEventNameMatch();
  const { data } = useGetUniversalBurgerMenuDataQuery({
    variables: {
      eventHandle: eventName || '',
    },
    batchMode: 'fast',
  });

  const eventPhoto = extractEventPhoto(data?.eventByName);
  const headerLayoutPhoto = extractHeaderLayoutPhoto(data?.eventByName);

  const eventTimestamp = data?.eventByName?.info.tentativeDate?.timestamp;

  const productRows = useProductRows(
    data?.eventByName?.pages ?? [],
    data?.eventByName?.productVerticals ?? {},
    data?.eventByName?.id,
    undefined,
    data?.eventByName?.pageContainers ?? [],
  );

  const { tq } = useTranslation('adminDashboardV3');

  const eventDate = useMemo(() => {
    return eventTimestamp ? format(parseISO(eventTimestamp), 'MMMM dd, yyyy') : undefined;
  }, [eventTimestamp]);

  const trackAndProvideLogout = useCallback(() => {
    telemetry.trackRegistryNavMenuItemClicked('accountSignOut');
    provideLogout({});
  }, [telemetry, provideLogout]);

  const items: BurgerMenuProps['items'] = useMemo(() => {
    const ret: BurgerMenuProps['items'] = productRows.map(row => {
      return {
        headerText: row.title,
        items: row.children.flatMap(rowChild => [
          {
            text: rowChild.title,
            icon: rowChild.icon,
            onClick: () => rowChild?.onClick?.(GLOBAL_NAV_MENU),
            newPillText: rowChild.hasNewPill ? tq`New` : undefined,
            notificationPill: rowChild.notificationPill,
            isDisabled: rowChild.isDisabled,
            pageVisibility: rowChild.pageVisibility,
            containerVisibility: rowChild.containerVisibility,
            path: rowChild.path,
            pathIsInJoyWeb: rowChild.pathIsInJoyWeb,
            subItems: rowChild.subItems && [
              {
                headerText: rowChild.title,
                items: rowChild.subItems?.map(subItem => {
                  return {
                    text: subItem.customTitle || subItem.title,
                    icon: subItem.icon,
                    newPillText: subItem.hasNewPill ? tq`New` : undefined,
                    onClick: () => subItem?.onClick?.(GLOBAL_NAV_MENU),
                    isDisabled: subItem.isDisabled,
                    pageVisibility: subItem.pageVisibility,
                    containerVisibility: subItem.containerVisibility,
                    path: subItem.path,
                    pathIsInJoyWeb: rowChild.pathIsInJoyWeb,
                    topDivider: subItem.hasDivider,
                  };
                }),
              },
            ],
          },
        ]),
      };
    });
    return ret;
  }, [productRows, tq]);

  return {
    items,
    trackAndProvideLogout,
    eventDate,
    location: data?.eventByName?.info.location,
    photoUrl: resolvePhotoUrl(eventPhoto, headerLayoutPhoto, '') || data?.eventByName?.photo?.url,
    eventId: data?.eventByName?.id,
    eventHandle: eventName,
  };
}
